// L'EFFACEUR — Structured Data (Schema.org JSON-LD)
// Permet à Google d'afficher des rich snippets (étoiles, prix, FAQ).
// À mettre dans <head> de la landing.
//
// Usage : <StructuredData type="organization" /> ou <StructuredData type="product" pack="audit" />

import { useEffect } from 'react';

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "L'EFFACEUR",
  "alternateName": "Leffaceur",
  "url": "https://effaceurpro.fr",
  "logo": "https://effaceurpro.fr/logo512.png",
  "description": "L'EFFACEUR automatise la résiliation et le transfert de contrats : déménagement, succession, audit pouvoir d'achat.",
  "foundingDate": "2026-04",
  "founders": [{ "@type": "Person", "name": "Alexis Soumebelle" }],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Mondonville",
    "addressRegion": "Haute-Garonne",
    "postalCode": "31700",
    "addressCountry": "FR",
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "leffaceur@effaceurpro.fr",
    "contactType": "customer service",
    "availableLanguage": "French",
  },
  "sameAs": [],
};

const PRODUCT_SCHEMAS = {
  audit: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mission Audit Pouvoir d'Achat",
    "provider": { "@type": "Organization", "name": "L'EFFACEUR" },
    "description": "Audit gratuit de vos relevés bancaires. Détection des abonnements oubliés et payés trop chers. Résiliation automatique. Vous payez 20% des économies réelles.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "description": "Audit gratuit. Commission 20% sur économies réelles uniquement.",
      },
      "availability": "https://schema.org/InStock",
    },
    "areaServed": { "@type": "Country", "name": "France" },
  },
  demenagement: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mission Déménagement",
    "provider": { "@type": "Organization", "name": "L'EFFACEUR" },
    "description": "Résiliation des anciens contrats + ouverture optimisée des nouveaux lors d'un déménagement.",
    "offers": {
      "@type": "Offer",
      "price": "129",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
    },
    "areaServed": { "@type": "Country", "name": "France" },
  },
  heritage: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mission Héritage",
    "provider": { "@type": "Organization", "name": "L'EFFACEUR" },
    "description": "Clôture des contrats du défunt et dossier notaire complet pour la succession.",
    "offers": {
      "@type": "Offer",
      "price": "149",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
    },
    "areaServed": { "@type": "Country", "name": "France" },
  },
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Combien coûte le Pack Audit ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'audit est 100% gratuit. Vous payez seulement 20% des économies réelles que nous vous faisons réaliser. Sans économies = sans frais.",
      },
    },
    {
      "@type": "Question",
      "name": "Comment fonctionne la résiliation par L'EFFACEUR ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Vous nous mandatez via une signature électronique légalement valide (article 1367 C.Civ). Nous envoyons les lettres de résiliation à chaque organisme par email ou LRAR selon les exigences.",
      },
    },
    {
      "@type": "Question",
      "name": "Mes données sont-elles sécurisées ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui. Données hébergées en Union Européenne, chiffrement TLS 1.3, conformité RGPD totale. Liste publique des sous-traitants disponible sur effaceurpro.fr/subprocessors.",
      },
    },
    {
      "@type": "Question",
      "name": "Puis-je annuler après avoir payé ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, vous disposez d'un droit de rétractation de 14 jours conformément aux articles L221-18 et suivants du Code de la consommation.",
      },
    },
  ],
};


const StructuredData = ({ type = 'organization', pack = null }) => {
  useEffect(() => {
    let schema;
    let id = 'leffaceur-jsonld';

    if (type === 'organization') schema = ORG_SCHEMA;
    else if (type === 'product' && pack) schema = PRODUCT_SCHEMAS[pack];
    else if (type === 'faq') schema = FAQ_SCHEMA;

    if (!schema) return;
    id = `leffaceur-jsonld-${type}-${pack || 'default'}`;

    // Remove existing
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [type, pack]);

  return null;
};

export default StructuredData;
